/**
 * Browser-Standard-Attribute, die grundsätzlich NICHT in den DataLayer übernommen werden.
 * Diese Liste wird von allen DataLayer-Komponenten verwendet (Custom Elements, Click-Events, etc.).
 *
 * Kategorien:
 * - Navigation: href, target, rel
 * - Styling: class, style
 * - Media: src, alt, title
 * - Verhalten: action
 * - DataLayer-spezifisch: dl-event (wird als Event-Name verwendet, nicht als Parameter)
 * - Verzögerte Events: success-signal-required, success-signal-timeout, dl-signal
 *
 * Hinweis: Formular-Attribute wie 'name' und 'value' werden NICHT gefiltert,
 * da sie im DataLayer-Kontext Bedeutung haben (z.B. Produkt-Name, Event-Name).
 * Ebenso wird 'id' NICHT gefiltert, da es als Event-Parameter wichtig ist (z.B. transaction_id bei purchase).
 */
export const IGNORED_ATTRIBUTES = [
	// Navigation & Links
	'href', 'target', 'rel',
	// Styling
	'class', 'style',
	// Media
	'src', 'alt', 'title',
	// Verhalten
	'action',
	// DataLayer-spezifisch
	'dl-event',
	// Verzögerte Events (success-signal-required): Steuerattribute, keine Event-Parameter
	'success-signal-required', 'success-signal-timeout', 'dl-signal'
];

/**
 * Zusätzliche Attribute für Click-Events, die bei Links/Buttons ignoriert werden sollten.
 * Diese umfassen:
 * - Attribute, die bereits separat in buildClickData() behandelt werden (component, interaction, caption)
 * - Formular-spezifische Attribute, die bei Links/Buttons keine Bedeutung haben
 * - Accessibility-Attribute für interaktive Elemente
 */
export const CLICK_CONTEXT_IGNORED_ATTRIBUTES = [
	// Bereits separat behandelt in buildClickData()
	'component', 'interaction', 'caption',
	// Formular-Attribute (bei Links/Buttons nicht relevant)
	'type', 'name', 'value', 'placeholder', 'autocomplete', 'autofocus', 'required',
	'min', 'max', 'step', 'pattern', 'minlength', 'maxlength', 'disabled', 'readonly',
	// Accessibility (bei interaktiven Elementen)
	'aria-label', 'aria-hidden', 'role', 'tabindex',
	// Media (bei interaktiven Elementen)
	'alt', 'title'
];

/**
 * Attribute die bei Custom Elements wie <dl-event>, <dl-pageview> etc. ignoriert werden sollten.
 * Diese Attribute werden bereits für andere Zwecke verwendet (z.B. "name" als Event-Name).
 */
export const CUSTOM_ELEMENT_IGNORED_ATTRIBUTES = [
	'name'  // Wird als Event-Name verwendet, nicht als Parameter
];

export const LEGACY_TRACKING_PREFIXES = ["product-", "tracking-", "cd-", "cm-"];

/**
 * Prefix für die Parameter aus dem "ers-metrik"-Cookie (eReader-Kontext),
 * z.B. `device=SHINE` → `ers_device`.
 */
export const ERS_METRIK_PARAM_PREFIX = "ers_";

/**
 * GA4 E-Commerce Events nach offiziellem Standard, ergänzt um remove_from_wishlist (kein GA4-Standard-Event)
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */
export const ECOMMERCE_EVENTS = [
	'add_payment_info',
	'add_shipping_info',
	'add_to_cart',
	'add_to_wishlist',
	'begin_checkout',
	'purchase',
	'refund',
	'remove_from_cart',
	'remove_from_wishlist',
	'select_item',
	'select_promotion',
	'view_cart',
	'view_item',
	'view_item_list',
	'view_promotion'
];

/**
 * Events, die ProductAttribution benötigen (aus localStorage)
 * Diese Events lesen item_list_id, item_list_name, index aus dem localStorage
 */
export const PRODUCT_ATTRIBUTION_EVENTS = [
	'add_to_cart',
	'begin_checkout',
	'purchase'
];

/**
 * GA4 Standard-Properties die immer erlaubt sind (werden nicht gefiltert)
 * Diese Properties sind Teil der offiziellen GA4 API
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 * @see https://support.google.com/analytics/table/13594742
 */
export const GA4_STANDARD_PROPERTIES = [
	// Common Event Parameters
	'engagement_time_msec', 'session_id', 'timestamp_micros',
	// E-Commerce Parameters
	'items', 'value', 'currency', 'transaction_id', 'affiliation',
	'coupon', 'shipping', 'tax', 'customer_type',
	// Item Parameters
	'item_id', 'item_name', 'item_brand', 'item_category', 'item_variant',
	'price', 'quantity', 'index', 'discount', 'location_id',
	'item_list_name', 'item_list_id',
	'item_category2', 'item_category3', 'item_category4', 'item_category5',
	// Promotion Parameters
	'creative_name', 'creative_slot', 'promotion_id', 'promotion_name',
	// Checkout Parameters
	'payment_type', 'shipping_tier',
	// Page View Parameters
	'page_title', 'page_location', 'page_referrer',
	// Video Parameters
	'video_current_time', 'video_duration', 'video_percent', 'video_provider',
	'video_title', 'video_url', 'visible',
	// Search Parameters
	'search_term',
	// Content Parameters
	'content_type', 'content_id',
	// Social/Group Parameters
	'group_id',
	// Gaming Parameters
	'virtual_currency_name'
];

/**
 * Erlaubte Prefixe für Custom Properties, deren vollständige Namen nicht fest
 * bekannt sind, weil sie aus einer dynamischen Quelle stammen.
 * Aktuell: `ers_*` aus dem "ers-metrik"-Cookie.
 */
export const ALLOWED_CUSTOM_PROPERTY_PREFIXES = [
	ERS_METRIK_PARAM_PREFIX
];

/**
 * Erlaubte Custom Properties für GA4 Events
 * Nur diese Custom Dimensions werden an GA4 gesendet
 * Alle anderen Properties werden vor dem gtag()-Aufruf gedroppt
 */
export const ALLOWED_CUSTOM_PROPERTIES = [
	"ab_test",
	"ab_test_search",
	"aenderunglieferadresse",
	"aenderungrechnungsanschrift",
	"authentifizierung",
	"caption",
	"client_id",
	"clubstatus",
	"color_scheme",
	"component",
	"content",
	"coveopipeline",
	"darstellung",
	"debug_data",
	"dialog_name",
	"ean",
	"einloesunghdbcredits",
	"environment",
	"exp_variant_string",
	"experiment_id",
	"filialsucheerfolg",
	"filter",
	"firmwareversion",
	"fusionpipeline",
	"fusionsuggestpipeline",
	"geraetekategorie",
	"hoerbuchdownloadkaufart",
	"impression",
	"interaction",
	"kategoriepfadname",
	"kundensegmente",
	"label",
	"lieblingsfiliale",
	"method",
	"perf_cls",
	"perf_fcp",
	"perf_fid",
	"perf_inp",
	"perf_lcp",
	"perf_ttfb",
	"produktab_test",
	"produktart",
	"produktkomponente",
	"produktseite",
	"produktsuchbegriff",
	"querystring",
	"rebuy_price",
	"reco_fallback",
	"referrer",
	"sale_contains_declined_items",
	"search_input",
	"search_redirect",
	"search_term_origin",
	"seitenart",
	"seitentyp",
	"spellcheckeraktiv",
	"spellcheckervorschlag",
	"suchbegriff",
	"suchbegriffersetzung",
	"suchbegriffersetzunguebersteuert",
	"suchergebnisse",
	"suchtreffer",
	"total_rebuy_price",
	"umsatzgeschenkkarten",
	"umsatzgeschenkservice",
	"umsatzgutscheine",
	"umsatzrabatte",
	"user_id",
	"user_logged_in",
	"utm_campaign",
	"utm_medium",
	"utm_source",
	"variant_id",
	"verfuegbarkeit",
	"verkaufsrang",
	"versandart",
	"viewport_width",
	"zahlart",
	"kunden_status"
];